import CompetitionService from '@/services/CompetitionService'
import moment from 'moment'

// Removed TOP_METRICS mock data - will be calculated from feedbackBreakdown data

const EXAMPLE_RESPONSE = {
  success: true,
  data: {
    feedbackBreakdown: [
      {
        label: 'Ovation Dev',
        feedbackCount: 243,
        avgOnlineRating: 4.5,
        categoryBreakdown: [
          {
            categoryId: '62782847ecd4c464fa4f6150',
            positiveMentionCount: 1,
            negativeMentionCount: 2,
            categoryName: 'Taste'
          }
        ],
        brandId: '5d02afeee2e45649bb4b56fb'
      },
      {
        label: 'The Spicy Spoon',
        feedbackCount: 232,
        avgOnlineRating: 4.5,
        categoryBreakdown: [
          {
            categoryId: '62782847ecd4c464fa4f6150',
            positiveMentionCount: 1,
            negativeMentionCount: 1,
            categoryName: 'Taste'
          },
          {
            categoryId: '62782848ecd4c464fa4f615a',
            positiveMentionCount: 0,
            negativeMentionCount: 1,
            categoryName: 'Portion'
          }
        ],
        brandId: '123'
      },
      {
        label: 'Bistro Bliss',
        feedbackCount: 5,
        avgOnlineRating: 4.5,
        categoryBreakdown: [
          {
            categoryId: '62782849ecd4c464fa4f6172',
            positiveMentionCount: 0,
            negativeMentionCount: 1,
            categoryName: 'Cleanliness'
          },
          {
            categoryId: '62782849ecd4c464fa4f6174',
            positiveMentionCount: 0,
            negativeMentionCount: 1,
            categoryName: 'Ambiance'
          },
          {
            categoryId: '6278284becd4c464fa4f619a',
            positiveMentionCount: 0,
            negativeMentionCount: 1,
            categoryName: 'Price'
          }
        ],
        brandId: '456'
      },
      {
        label: 'Savory Delights',
        brandId: '789',
        feedbackCount: 0,
        avgOnlineRating: 0,
        categoryBreakdown: []
      },
      {
        label: 'Culinary Haven',
        feedbackCount: 6,
        avgOnlineRating: 4.6,
        categoryBreakdown: [
          {
            categoryId: '62782848ecd4c464fa4f6157',
            positiveMentionCount: 1,
            negativeMentionCount: 1,
            categoryName: 'Temperature'
          }
        ],
        brandId: '059'
      }
    ]
  }
}

export default {
  namespaced: true,
  state: {
    competitors: [],
    lastSummaryURL: '/competition/summary',
    filters: null,
    feedbackBreakdown: []
  },
  getters: {
    selectCompetitors: (state) => state.competitors,
    selectCompetitorNames: (state) => state.competitors.map((c) => c.name),
    selectCompetitorOptions: (state) =>
      state.competitors.map((c) => ({ label: c.name, value: c._id })),
    selectLastSummaryURL: (state) => state.lastSummaryURL,
    selectFilters: (state) => state.filters,
    selectTopMetrics: (state) => {
      // Calculate top metrics from feedbackBreakdown data
      if (!state.feedbackBreakdown || !Array.isArray(state.feedbackBreakdown)) {
        return []
      }

      return (
        state.feedbackBreakdown
          // .filter((item) => item.feedbackCount > 0) // Only include items with feedback
          .map((item) => {
            // Calculate metrics for each category
            const categoryMetrics = item.categoryBreakdown.map((category) => {
              const totalMentions = category.positiveMentionCount + category.negativeMentionCount
              const positiveSentimentPercentage =
                item.feedbackCount > 0
                  ? Math.round((category.positiveMentionCount / item.feedbackCount) * 100)
                  : 0
              const negativeSentimentPercentage =
                item.feedbackCount > 0
                  ? Math.round((category.negativeMentionCount / item.feedbackCount) * 100)
                  : 0

              return {
                categoryId: category.categoryId,
                categoryName: category.categoryName,
                positiveSentimentPercentage,
                negativeSentimentPercentage,
                totalMentions,
                positiveMentionCount: category.positiveMentionCount,
                negativeMentionCount: category.negativeMentionCount
              }
            })

            // Sort by positive percentage for top strengths (descending)
            const topStrengths = categoryMetrics
              .filter((metric) => metric.positiveSentimentPercentage > 0)
              .sort((a, b) => b.positiveSentimentPercentage - a.positiveSentimentPercentage)
              .slice(0, 3)

            // Sort by negative percentage for top weaknesses (descending)
            const topWeaknesses = categoryMetrics
              .filter((metric) => metric.negativeSentimentPercentage > 0)
              .sort((a, b) => b.negativeSentimentPercentage - a.negativeSentimentPercentage)
              .slice(0, 3)

            return {
              _id: item.competitorId || item.label, // Use competitorId if available, otherwise use label
              name: item.label,
              topStrengths,
              topWeaknesses,
              feedbackCount: item.feedbackCount,
              avgOnlineRating: item.avgOnlineRating || 0 // Will be available in future
            }
          })
      )
    },
    selectHeatmapCategories: (_, getters, rootState, rootGetters) => {
      return rootGetters['insightsv2/selectNonOtherCategories'].reduce(
        (acc, curr) => {
          if (!curr.path) {
            acc.push(curr._id)
          }
          return acc
        },
        ['Reviews', 'Avg Online Rating']
      )
    },
    selectCategoryName: (_, getters, rootState, rootGetters) => (categoryId) => {
      // Handle special cases
      if (['Reviews', 'Avg Online Rating'].includes(categoryId)) {
        return categoryId
      }

      // Get category name from insightsv2 store
      const categories = rootGetters['insightsv2/selectNonOtherCategories']
      const foundCategory = categories.find((cat) => cat._id === categoryId)
      return foundCategory ? foundCategory.name : categoryId
    },
    selectHeatmapSeries(state, getters, rootState, rootGetters) {
      const feedbackBreakdown = state.feedbackBreakdown
      const categories = getters.selectHeatmapCategories
      const parentMap = rootGetters['insightsv2/selectCategoryParentMap']

      if (!feedbackBreakdown || !Array.isArray(feedbackBreakdown)) {
        return []
      }

      const seriesData = feedbackBreakdown.map((item) => {
        // Initialize data arrays
        const data = new Array(categories.length).fill(0)
        const counts = new Array(categories.length).fill(0)

        // Set reviews count and average rating in first two columns
        data[0] = -1 // Use -1 to keep column white
        counts[0] = item.feedbackCount || 0

        data[1] = -1 // Use -1 to keep column white
        counts[1] = item.avgOnlineRating || 0

        // Create a map to aggregate negative counts by effective category
        const categoryAggregates = {}

        // First aggregate all negative counts by effective category
        if (item.categoryBreakdown && Array.isArray(item.categoryBreakdown)) {
          item.categoryBreakdown.forEach((cat) => {
            const effectiveCategoryId = parentMap[cat.categoryId] || cat.categoryId
            if (!categoryAggregates[effectiveCategoryId]) {
              categoryAggregates[effectiveCategoryId] = 0
            }
            categoryAggregates[effectiveCategoryId] += cat.negativeMentionCount
          })
        }

        // Then set the aggregated counts and calculate percentages
        Object.entries(categoryAggregates).forEach(([categoryId, totalNegativeCount]) => {
          const categoryIndex = categories.indexOf(categoryId)
          if (categoryIndex > 1) {
            // Skip first two columns (Reviews and Avg Online Rating)
            counts[categoryIndex] = totalNegativeCount
            if (item.feedbackCount) {
              const percentage = Math.ceil((totalNegativeCount / item.feedbackCount) * 100)
              data[categoryIndex] = percentage <= 100 ? percentage : 100
            }
          }
        })

        const heatmapData = {
          name: item.label,
          competitorId: item.competitorId, // Use competitorId from API response
          data,
          counts,
          totalCount: item.feedbackCount || 0
        }
        return heatmapData
      })

      return seriesData.reverse()
    }
  },
  mutations: {
    SET_LAST_SUMMARY_URL(state, url) {
      state.lastSummaryURL = url
    },
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    SET_COMPETITORS(state, competitors) {
      state.competitors = competitors
    },
    SET_FEEDBACK_BREAKDOWN(state, feedbackBreakdown) {
      state.feedbackBreakdown = feedbackBreakdown
    }
  },
  actions: {
    async applyFilters({ commit }, filters) {
      commit('SET_FILTERS', filters)
    },

    async setLastSummaryUrl({ commit }, url) {
      commit('SET_LAST_SUMMARY_URL', url)
    },

    formatFilters({ getters, _, rootGetters }) {
      const filters = getters.selectFilters
      const payloadFilters = {
        createdAtRange: filters.dateRange || [
          moment().startOf('d').subtract(30, 'd').utc().format(),
          moment().endOf('day').utc().format()
        ],
        locationIds: filters.locations || undefined,
        groupIds: filters.groups || undefined,
        competitorIds: filters.competitors || undefined,
        companyIds: [rootGetters['company/selectActiveCompany']._id],
        isSummary: true // this is only true if the request is coming from the summary page
      }
      return payloadFilters
    },

    async fetchBreakdown({ commit, dispatch }) {
      try {
        const response = await CompetitionService.fetchBreakdown({
          filters: await dispatch('formatFilters')
        })

        if (response.body.success) {
          commit('SET_FEEDBACK_BREAKDOWN', response.body.data.feedbackBreakdown)
        }
      } catch (error) {
        console.error('Error fetching breakdown:', error)
        // Fallback to example data for now
        // commit('SET_FEEDBACK_BREAKDOWN', EXAMPLE_RESPONSE.data.feedbackBreakdown)
      }
    },

    async fetchCompetitors({ commit, rootGetters }) {
      try {
        const companyId = rootGetters['company/selectActiveCompany']._id
        const response = await CompetitionService.listCompetitors({ companyId })

        commit('SET_COMPETITORS', response.body.data.competitors)
      } catch (error) {
        console.error('Error fetching competitors:', error)
        commit('SET_COMPETITORS', [])
      }
    }
  }
}
